import { useEffect, useState } from "react";
import {
  ArrowRight,
  Bot,
  Loader2,
  MessageCircleQuestion,
  Send,
  Sparkles,
  UserRound,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const SAMPLE_QUESTIONS = [
  "How should we prepare for an AGM?",
  "Draft a polite maintenance reminder",
  "What records should a Society maintain?",
  "How should we organize complaints?",
];

const TOPICS = [
  "Meetings",
  "Notices",
  "Administration",
  "Maintenance",
  "Records",
  "Complaints",
];

export default function PublicAskExperience({ onRegister }) {
  const [question, setQuestion] = useState("");
  const [asked, setAsked] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/public/stats")
      .then((response) => setStats(response.data))
      .catch(() => setStats(null));
  }, []);

  const ask = async (suggestedQuestion = "") => {
    const value = (suggestedQuestion || question).trim();

    if (!value) {
      toast.error("Type a Society question to get started.");
      return;
    }

    setQuestion(value);
    setAsked(value);
    setAsking(true);
    setAnswer("");
    setError("");

    try {
      const response = await api.post("/public/ask", { question: value });
      setAnswer(response.data.answer);
      setRemaining(response.data.remaining);
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail ||
          "We could not answer that right now. Please try again.",
      );
      if (requestError.response?.status === 429) {
        setRemaining(0);
      }
    } finally {
      setAsking(false);
    }
  };

  return (
    <section
      className="relative scroll-mt-20 overflow-hidden bg-blue-950 py-20 sm:py-24"
      data-testid="premium-ask-section"
      id="ask-your-society-mitra"
    >
      <div className="absolute inset-0 opacity-25 [background-image:linear-gradient(rgba(255,255,255,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.12)_1px,transparent_1px)] [background-size:42px_42px]" />
      <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-orange-500/30 blur-3xl" />
      <div className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-blue-500/40 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-8">
        <div className="text-white">
          <span className="inline-flex items-center gap-2 rounded-full border border-orange-300/40 bg-orange-500/15 px-3 py-1.5 text-xs font-bold tracking-[0.14em] text-orange-200">
            <Sparkles className="h-4 w-4 text-orange-300" />
            ASK YOUR SOCIETY MITRA
          </span>
          <h2 className="mt-6 font-display text-4xl font-black leading-[1.02] sm:text-5xl lg:text-6xl">
            Society Question?
            <span className="block text-orange-300">Just Ask.</span>
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-blue-100 sm:text-lg">
            Get instant AI-powered informational guidance for everyday Housing
            Society questions — meetings, notices, administration, maintenance,
            records and more.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {TOPICS.map((topic) => (
              <span
                className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold text-blue-50"
                key={topic}
              >
                {topic}
              </span>
            ))}
          </div>
          <p className="mt-8 text-xs font-bold tracking-[0.14em] text-blue-200">
            TRY A QUESTION
          </p>
          <div
            className="mt-3 grid gap-2 sm:grid-cols-2"
            data-testid="ask-preview-samples"
          >
            {SAMPLE_QUESTIONS.map((sample, index) => (
              <button
                className="group flex items-center justify-between gap-3 rounded-2xl border border-white/15 bg-white/[0.07] px-4 py-3 text-left text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                data-testid={`ask-preview-sample-${index}`}
                disabled={asking}
                key={sample}
                onClick={() => ask(sample)}
                type="button"
              >
                <span className="flex items-center gap-2">
                  <MessageCircleQuestion className="h-4 w-4 shrink-0 text-orange-300" />
                  {sample}
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-blue-200 transition-transform group-hover:translate-x-1" />
              </button>
            ))}
          </div>
          {stats && (
            <div
              className="mt-8 flex flex-wrap gap-5 text-sm text-blue-100"
              data-testid="premium-ask-stats"
            >
              <span className="inline-flex items-center gap-2">
                <Users className="h-4 w-4 text-emerald-300" />
                <strong>{stats.societies.toLocaleString("en-IN")}</strong>{" "}
                societies onboard
              </span>
              <span className="inline-flex items-center gap-2">
                <Users className="h-4 w-4 text-orange-300" />
                <strong>{stats.members.toLocaleString("en-IN")}</strong> homes
                managed
              </span>
            </div>
          )}
        </div>

        <div
          className="relative overflow-hidden rounded-[28px] border border-white/20 bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
          data-testid="promo-ask"
        >
          <div className="flex items-center gap-3 border-b border-slate-100 bg-gradient-to-r from-blue-950 to-blue-800 px-5 py-4 text-white">
            <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-rose-500 shadow-lg">
              <Bot className="h-5 w-5" />
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-blue-900 bg-emerald-400" />
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-lg font-bold leading-tight sm:text-xl">
                Ask Your Society Mitra
              </h3>
              <p className="text-xs text-blue-200">
                Apka Society Mitra • Housing Society AI guidance
              </p>
            </div>
          </div>

          <div className="space-y-4 bg-slate-50 px-5 py-6 sm:px-7">
            <ChatBubble role="mitra">
              Namaste! I am your Society Mitra. Ask me anything about everyday
              Housing Society work — meetings, notices, maintenance, records or
              complaints.
            </ChatBubble>

            {asked && (asking || answer || error) && (
              <ChatBubble role="user">{asked}</ChatBubble>
            )}

            {asking && (
              <ChatBubble role="mitra">
                <span
                  className="inline-flex items-center gap-2 text-slate-500"
                  data-testid="ask-preview-loading"
                >
                  <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                  Your Society Mitra is thinking…
                </span>
              </ChatBubble>
            )}

            {error && (
              <div
                className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"
                data-testid="ask-preview-error"
              >
                {error}
                <button
                  className="ml-2 font-bold underline underline-offset-2"
                  data-testid="ask-preview-retry"
                  onClick={() => ask()}
                  type="button"
                >
                  Retry
                </button>
              </div>
            )}

            {answer && (
              <div data-testid="ask-preview-answer">
                <ChatBubble role="mitra">
                  <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700">
                    {answer}
                  </p>
                </ChatBubble>
                <div className="mt-3 flex flex-wrap items-center gap-3 pl-11">
                  <Button
                    className="h-9 rounded-full bg-blue-900 text-xs text-white hover:bg-blue-800"
                    data-testid="ask-preview-register"
                    onClick={onRegister}
                    type="button"
                  >
                    Register free to ask more{" "}
                    <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </Button>
                  {remaining !== null && (
                    <span className="text-xs text-slate-500">
                      {remaining} free preview question
                      {remaining === 1 ? "" : "s"} left today
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 bg-white px-5 py-5 sm:px-7">
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                className="h-12 rounded-full border-slate-200 bg-slate-50 px-5 text-slate-900"
                data-testid="ask-preview-input"
                maxLength={500}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && !asking && ask()}
                placeholder="Type your Society question here..."
                value={question}
              />
              <Button
                className="h-12 shrink-0 rounded-full bg-orange-500 px-5 text-white hover:bg-orange-600"
                data-testid="ask-preview-submit"
                disabled={asking}
                onClick={() => ask()}
                type="button"
              >
                {asking ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                <span className="ml-2">Ask Society Mitra</span>
              </Button>
            </div>
            <p
              className="mt-4 text-xs leading-relaxed text-slate-500"
              data-testid="ask-preview-disclaimer"
            >
              AI-generated guidance is informational and should not replace
              qualified legal, financial, structural or other professional
              advice.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChatBubble({ children, role }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex items-end gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isUser ? "bg-blue-100 text-blue-800" : "bg-gradient-to-br from-orange-500 to-rose-500 text-white"}`}
      >
        {isUser ? (
          <UserRound className="h-4 w-4" />
        ) : (
          <Bot className="h-4 w-4" />
        )}
      </span>
      <div
        className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed shadow-sm ${isUser ? "rounded-2xl rounded-br-sm bg-blue-900 text-white" : "rounded-2xl rounded-bl-sm border border-slate-200 bg-white text-slate-700"}`}
      >
        {children}
      </div>
    </div>
  );
}
